<script lang="ts" setup>
import { ref } from 'vue'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import FormButton from '@/components/common/FormButton.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import AverageSearchForm from '@/components/forms/AverageSearchForm.vue'
import AverageSearchResults from '@/components/widgets/public/AverageSearchResults.vue'
import { useAveragesStore } from '@/store/public/averages'
import { getErrorMessage } from '@/utils/error-handling'

const averages = useAveragesStore()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

async function fetch({ bowler, club }: { bowler: string; club: string }) {
  isLoading.value = true

  try {
    await averages.fetch(bowler, club)
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed searching for averages: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
}

async function fetchMore() {
  isLoading.value = true

  try {
    await averages.fetchMore()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching more results: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
}
</script>

<template>
  <main>
    <AverageSearchForm :is-loading="isLoading" @submit="fetch" />

    <NotificationBanner
      v-if="!isLoading && errorMessage"
      :message="errorMessage"
      is-danger
    />

    <AverageSearchResults />

    <div v-if="!isLoading && averages.next" class="fetch-more-button-container">
      <FormButton @click.prevent="fetchMore">
        {{ $t('averages.fetch-more') }}
      </FormButton>
    </div>

    <div v-if="isLoading" class="loading-animation">
      <BusyIndicator />
    </div>
  </main>
</template>

<style lang="scss" scoped>
.loading-animation {
  padding: 2em 0;
  font-size: 3em;
  text-align: center;
}
</style>
